import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import { Component, computed, effect, inject, OnDestroy, signal, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, forkJoin, of, Subscription, tap, interval } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { MachineTypeDTO } from 'src/app/modules/shared/interface/machine-type.entity';
import { OperatorPerformanceDTO } from 'src/app/modules/shared/interface/operator.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { MachineTypeService } from 'src/app/modules/shared/services/machine-type.service';
import { OperatorService } from 'src/app/modules/shared/services/operator.service';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { AuthService } from 'src/app/core';
import { OperatorDashboardSocketService } from '../../services/operatorDashboardSocket.service';
import { OperatorDashboardCurrentJobDTO, OperatorShiftReportDTO } from 'src/app/modules/shared/interface/machine.entity';
import { ShiftRosterService } from 'src/app/modules/job-operation/services/shift-roster.service';
import { ShiftRosterDTO } from 'src/app/modules/job-operation/interfaces/shift-roster.entity';
import { ShiftService } from 'src/app/modules/shared/services/shift.service';
import { shiftDTO } from 'src/app/modules/shared/interface/shift.entity';
import { OperationShiftService } from 'src/app/modules/job-operation/services/operation-shift.service';

/** Combined card data merged from APIs */
export interface MachineCardData {
  machineId:         number;
  machineName:       string;
  machineStatus:     string;
  // from getDailyReport
  plannedImpression: number;
  actualImpression:  number;
  unitOfMachine:     string;
  progressPct:       number;
  // from getMachineCurrentShifts
  currentShift:      string;
  currentShiftId:    number;
  shiftDate:         string;
  shiftStartTime:    string;   // e.g. '06:00:00'
  shiftEndTime:      string;   // e.g. '18:00:00'
  // from getDailyOperationTime
  operationTime:     number;
  idleTime:          number;
  maintenanceTime:   number;
  holdTime:          number;
  makeReadyTime:     number;
  // from getDailyReport
  makeReadyCount:    number;
  shiftWiseDetails:  { shiftId: number; shiftName: string; actualOutput: number; makeReadyCount: number }[];
  // from JobOperationService.search
  currentJob:        OperatorDashboardCurrentJobDTO | null;
  // from ShiftRosterService — today's roster for this machine
  currentRosterOperators: string[];
  todayRosters:           ShiftRosterDTO[];
  // from previous shift API
  previousShiftOutput:    number | null;
  previousShiftOperators: string[];
  previousShiftDate:      string | null;
  previousShiftName:      string | null;
  
  // from machine shift output API
  monthlyTotalOutput:      number | null;
  monthlyTotalMakeReady:   number | null;
  monthlyShiftOutputs:     { shiftId: number; shiftName: string; monthlyOutput: number; makeReadyCount: number; isBest: boolean }[];
}

@Component({
  selector: 'app-operator-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './operator-dashboard.component.html',
  styleUrl: './operator-dashboard.component.scss',
})
export class OperatorDashboardComponent implements OnDestroy {

  machineTypeService  = inject(MachineTypeService);
  notification        = inject(NotificationService);
  operatorService     = inject(OperatorService);
  departmentService   = inject(DepartmentService);
  machineService      = inject(MachineService);
  shiftRosterService  = inject(ShiftRosterService);
  shiftService        = inject(ShiftService);
  operationShiftService = inject(OperationShiftService);

  router              = inject(Router);
  authService         = inject(AuthService);
  private wsService   = inject(OperatorDashboardSocketService);



  protected machineTypes        = signal<MachineTypeDTO[]>([]);
  protected operatorPerformance = signal<OperatorPerformanceDTO[]>([]);
  protected operatorNcrCounts   = signal<Record<string, number>>({});
  protected operatorNcrData     = signal<Record<string, any[]>>({});
  protected operatorNcrTotals   = signal<Record<string, number>>({});
  activeTab                     = signal<MachineTypeDTO | null>(null);
  protected $$departmentDTO     = signal<Department[]>([]);
  protected departmentId        = signal<number | null>(null);
  protected selectedYear        = signal<number>(new Date().getFullYear());
  protected selectedMonth       = signal<number>(new Date().getMonth() + 1);
  protected years: number[]     = [];
  private lastParams            = '';

  protected wsConnected     = signal<boolean>(false);
  protected liveUpdateFlash = signal<boolean>(false);
  protected lastUpdatedTime = signal<Date | null>(null);
  protected isDark          = signal<boolean>(false);

  /** Machine cards built from two APIs */
  protected machineCards    = signal<MachineCardData[]>([]);
  protected currentCardPage = signal<number>(0);
  protected cardsLoading    = signal<boolean>(false);
  private lastCardKey       = '';

  protected visibleMachineCards = computed(() => {
    const cards = this.machineCards();
    const page = this.currentCardPage();
    const size = 3;
    const start = page * size;
    return cards.slice(start, start + size);
  });

  private wsSubs: Subscription[] = [];
  private autoScrollInterval?: any;
  private cardRotationInterval?: any;
  operatorShiftReport: OperatorShiftReportDTO[] = [];

  @ViewChild('tableWrap') tableWrap?: ElementRef<HTMLDivElement>;

  today: string = '';
  currentTime: string = '';
  private clockSub?: Subscription;
  private refreshSub?: Subscription;

  protected searchParams = computed(() => ({
    year:        this.selectedYear(),
    month:       this.selectedMonth(),
    machineType: this.activeTab()?.id || 2,
    department:  this.departmentId(),
  }));

  months = [
    { value: 1,  label: 'January'   },
    { value: 2,  label: 'February'  },
    { value: 3,  label: 'March'     },
    { value: 4,  label: 'April'     },
    { value: 5,  label: 'May'       },
    { value: 6,  label: 'June'      },
    { value: 7,  label: 'July'      },
    { value: 8,  label: 'August'    },
    { value: 9,  label: 'September' },
    { value: 10, label: 'October'   },
    { value: 11, label: 'November'  },
    { value: 12, label: 'December'  },
  ];

  protected selectedMonthName = computed(() => {
    const m = this.months.find(x => x.value === this.selectedMonth());
    return m ? m.label : 'MONTH';
  });

  protected isVisible = signal<boolean>(true);
  protected duplicateRows = signal<boolean>(false);

  protected allowedContext = computed(() => {
    if (this.authService.hasContextPermission('operator-dashboard', 'all')) return null;

    let context: string[] = [];
    this.authService.getAuthState().subscribe(state => {
      const perm = state.permissions.find(p => p.resourcePath === 'operator-dashboard');
      if (perm) context = perm.context as unknown as string[];
    }).unsubscribe();

    if (context.length === 0) return null;

    const parts = context.map((v: string) => Number(v)).filter((n: number) => !isNaN(n) && n > 0);
    if (parts.length === 0) return null;

    return {
      departmentId:   parts[0],
      machineTypeIds: parts.slice(1),
    };
  });

  protected visibleDepartments = computed(() => {
    const ctx = this.allowedContext();
    const all = this.$$departmentDTO();
    if (!ctx) return all;
    return all.filter((d: Department) => d.departmentId === ctx.departmentId);
  });

  protected filteredMachineTypes = computed(() => {
    const deptId = this.departmentId();
    if (!deptId) return [];

    const ctx    = this.allowedContext();
    const byDept = this.machineTypes().filter((mt: MachineTypeDTO) => mt.departmentId === deptId);

    if (!ctx) return byDept;
    return byDept.filter((mt: MachineTypeDTO) => ctx.machineTypeIds.includes(mt.id));
  });

  constructor() {
    this.getAllMachineTypes();
    this.fetchDepartment();

    // ✅ Set up WS listeners first — completely decoupled from routing/data fetch
    this.initWebSocketListeners();

    effect(() => {
      // Reset duplicate flag whenever operators change to re-evaluate overflow
      this.operatorPerformance();
      this.duplicateRows.set(false);
    }, { allowSignalWrites: true });

    effect(() => {
      const ctx   = this.allowedContext();
      const types = this.machineTypes();
      const depts = this.$$departmentDTO();

      if (types.length === 0 || depts.length === 0) return;

      if (ctx) {
        this.departmentId.set(ctx.departmentId);
      } else if (!this.departmentId()) {
        this.departmentId.set(depts[0]?.departmentId ?? null);
      }
    });

    effect(() => {
      const deptId = this.departmentId();
      const types  = this.machineTypes();

      if (!deptId || types.length === 0) {
        this.activeTab.set(null);
        return;
      }

      const filtered = this.filteredMachineTypes();
      this.activeTab.set(filtered.length > 0 ? filtered[0] : null);
    });

    effect(() => {
      const params = this.searchParams();
      if (!params.machineType || !params.department) return;

      const key = JSON.stringify(params);
      if (this.lastParams === key) return;
      this.lastParams = key;

      // ✅ HTTP fetch always runs — independent of WS
      this.fetchPerformanceData();

      // ✅ WS subscribe is fire-and-forget — failure never blocks page
      this.trySubscribeWebSocket(params);
    });

    // React to department + active tab changes to refresh machine cards
    effect(() => {
      const deptId  = this.departmentId();
      const tabName = this.activeTab()?.machineTypeName ?? null;
      const year    = this.selectedYear();
      const month   = this.selectedMonth();

      if (!deptId || !tabName) {
        this.machineCards.set([]);
        return;
      }

      const cardKey = `${deptId}-${tabName}-${year}-${month}`;
      if (this.lastCardKey === cardKey) return;
      this.lastCardKey = cardKey;

      this.fetchMachineCards(deptId, tabName);
    });
  }

  ngOnInit(): void {
    const now = new Date();
    this.today = now.toISOString().split('T')[0];

    this.clockSub = interval(1000).subscribe(() => {
      this.currentTime = new Date().toLocaleTimeString();
    });

    // Removed auto-refresh

    const currentYear = new Date().getFullYear();
    for (let y = 2025; y <= currentYear; y++) {
      this.years.push(y);
    }
    if (this.selectedYear() < 2025) {
      this.selectedYear.set(2025);
    }

    this.startAutoScroll();
    this.startCardRotation();
  }

  ngOnDestroy(): void {
    this.clockSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.stopAutoScroll();
    this.stopCardRotation();
    // ✅ pause() not disconnect() — root singleton service stays alive
    this.wsService.pause();
    this.wsSubs.forEach(s => s.unsubscribe());
  }

  // ─── Machine Cards ────────────────────────────────────────────────────────

  /**
   * Calls each API exactly ONCE for the selected department, then merges
   * locally — no per-machine loops.
   *
   * 1. getMachineCurrentShifts(deptId)  → MachineCurrentShiftMachineDTO[] for all dept machines
   * 2. Per-machine: getDailyReport, getDailyOperationTime, getCurrentJob
   *
   * Then filter result sets to only the machines that belong to the active
   * machine-type tab and build MachineCardData[].
   */
  fetchMachineCards(deptId: number, machineTypeName: string, isSilent = false): void {
    const dept = this.$$departmentDTO().find(d => d.departmentId === deptId);
    if (!dept) return;

    // Machines belonging to the active tab
    const tabMachines: Machine[] = dept.machines.filter(
      m => m.machineTypeName?.toLowerCase() === machineTypeName.toLowerCase()
    );

    if (tabMachines.length === 0) {
      this.machineCards.set([]);
      this.currentCardPage.set(0);
      return;
    }

    if (!isSilent) {
      this.cardsLoading.set(true);
    }
    this.currentCardPage.set(0);

    const today = new Date();
    const dateParams = {
      year:  today.getFullYear(),
      month: today.getMonth() + 1,
      day:   today.getDate(),
    };

    // Today's date string for roster matching (YYYY-MM-DD)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // ── Department-level calls (1 each) + per-machine timeline, impression & job calls ──────
    forkJoin({
      shifts: this.machineService.getMachineCurrentShifts(deptId).pipe(
        catchError(() => of(null))
      ),
      // Fetch all shifts for this dept so we can pull start/end times
      shiftsAll: this.shiftService.getAll(deptId).pipe(
        catchError(() => of(null))
      ),
      timelines: forkJoin(
        tabMachines.map(m =>
          this.machineService.getDailyOperationTime(m.machineId, dateParams).pipe(
            catchError(() => of(null))
          )
        )
      ),
      dailyImpressions: forkJoin(
        tabMachines.map(m =>
          this.machineService.getDailyReport(m.machineId, dateParams).pipe(
            catchError(() => of(null))
          )
        )
      ),
      currentJobs: forkJoin(
        tabMachines.map(m =>
          this.machineService.getOperatorDashboardCurrentJob(m.machineId).pipe(
            catchError(() => of(null))
          )
        )
      ),
      // Fetch today's shift roster for the department (single call)
      roster: this.shiftRosterService.getShiftRosterByDep({
        department: deptId,
        year:  today.getFullYear(),
        month: today.getMonth() + 1,
      }).pipe(
        catchError(() => of(null))
      ),
    }).subscribe({
      next: ({ shifts, shiftsAll, timelines, dailyImpressions, currentJobs, roster }) => {
        const allShifts: any[] = shifts?.data?.machines ?? [];
        // Build shiftId -> shiftDTO map for start/end time lookup
        const shiftDefs: shiftDTO[]  = shiftsAll?.data?.shifts    ?? [];
        const shiftDefMap = new Map<number, shiftDTO>();
        shiftDefs.forEach(s => shiftDefMap.set(s.shiftId, s));

        // Build machineId → today's roster entries map from the roster response
        const rosterMachines: any[] = roster?.data?.machines ?? [];
        const rosterMap = new Map<number, ShiftRosterDTO[]>();
        rosterMachines.forEach((rm: any) => {
          const todayEntries: ShiftRosterDTO[] = (rm.shiftRoster ?? []).filter(
            (r: ShiftRosterDTO) => r.date === todayStr && r.rosterStatus === 'ACTIVE'
          );
          if (todayEntries.length > 0) {
            rosterMap.set(rm.machineId, todayEntries);
          }
        });

        const cards: MachineCardData[] = tabMachines.map((machine, i) => {
          const shift = allShifts.find(
            (m: any) => m.machineId === machine.machineId
          );
          const tl    = timelines[i]?.data ?? null;
          const di    = dailyImpressions[i]?.data ?? null;
          const cJob  = currentJobs[i]?.data ?? null;

          const planned = +(di?.plannedImpressions ?? 0);
          const actual  = +(di?.actualImpressions  ?? 0);
          const pct     = planned > 0 ? Math.round((actual / planned) * 100) : 0;

          const currentShiftName = shift?.currentShift ?? '—';
          const currentShiftId   = shift?.currentShiftId ?? 0;
          const shiftDef         = shiftDefMap.get(currentShiftId);
          const todayRosters     = rosterMap.get(machine.machineId) ?? [];

          // Operators assigned to the current active shift today
          const currentRosterOperators = todayRosters
            .filter(r => r.shift?.toLowerCase() === currentShiftName?.toLowerCase())
            .map(r => r.operator)
            .filter(Boolean);

          return {
            machineId:               machine.machineId,
            machineName:             machine.machineName,
            machineStatus:           machine.machineStatus ?? 'unknown',
            plannedImpression:       planned,
            actualImpression:        actual,
            unitOfMachine:           di?.unitOfMachine  ?? '',  
            progressPct:             pct,
            currentShift:            currentShiftName,
            currentShiftId,
            shiftDate:               shift?.shiftDate    ?? '',
            shiftStartTime:          shiftDef?.startTime ?? '',
            shiftEndTime:            shiftDef?.endTime   ?? '',
            operationTime:           +(tl?.operation_time   ?? 0),
            idleTime:                +(tl?.idle_time         ?? 0),
            maintenanceTime:         +(tl?.maintenance_time  ?? 0),
            holdTime:                +(tl?.hold_time         ?? 0),
            makeReadyTime:           +(tl?.make_ready_time   ?? 0),
            makeReadyCount:          +(di?.makeReadyCount    ?? 0),
            shiftWiseDetails:        di?.shiftWiseDetails    ?? [],
            currentJob:              cJob,
            currentRosterOperators,
            todayRosters,
            previousShiftOutput:     null,
            previousShiftOperators:  [],
            previousShiftDate:       null,
            previousShiftName:       null,
            monthlyTotalOutput:      null,
            monthlyTotalMakeReady:   null,
            monthlyShiftOutputs:     [],
          };
        });

        this.machineCards.set(cards);
        this.cardsLoading.set(false);
        this.lastUpdatedTime.set(new Date());

        // ── Fetch previous shift output per machine (non-blocking) ────────────
        this.fetchPreviousShiftOutputs(cards, dateParams, shiftDefMap);
        this.fetchMachineMonthlyShiftOutputs(cards, { year: this.selectedYear(), month: this.selectedMonth() });
      },
      error: () => {
        this.cardsLoading.set(false);
        this.notification.set({ type: 'error', message: 'Failed to load machine card data.' });
      },
    });
  }

  /** Returns all unique shift names rostered today for a given machine */
  getRosterShifts(card: MachineCardData): { shift: string; operators: string[]; isCurrent: boolean }[] {
    const shiftMap = new Map<string, string[]>();
    card.todayRosters.forEach(r => {
      if (!r.shift) return;
      const key = r.shift;
      if (!shiftMap.has(key)) shiftMap.set(key, []);
      shiftMap.get(key)!.push(r.operator);
    });
    return Array.from(shiftMap.entries()).map(([shift, operators]) => ({
      shift,
      operators,
      isCurrent: shift.toLowerCase() === card.currentShift?.toLowerCase(),
    }));
  }

  /**
   * Fetch previous shift output for each machine card after the cards are loaded.
   * Updates cards in-place so the UI reacts reactively.
   */
  private fetchPreviousShiftOutputs(
    cards: MachineCardData[],
    dateParams: { year: number; month: number; day: number },
    shiftDefMap: Map<number, shiftDTO>
  ): void {
    cards.forEach(card => {
      if (!card.currentShiftId) return;
      this.operationShiftService.getPreviousShiftOutput({
        year:    dateParams.year,
        month:   dateParams.month,
        day:     dateParams.day,
        shift:   card.currentShiftId,
        machine: card.machineId,
      }).pipe(catchError(() => of(null))).subscribe(res => {
        if (!res?.data) return;
        const prevOutput    = +(res.data.totalOutput ?? 0);
        const prevOperators = (res.data.operators ?? []).map((o: any) => o.name as string);
        const prevDate      = (res.data.previousShiftDate as string) ?? null;
        const prevShiftId   = +(res.data.previousShiftId ?? 0);
        const prevShiftName = shiftDefMap.get(prevShiftId)?.shiftName ?? null;
        // Update the specific card in the signal immutably
        this.machineCards.update(existingCards =>
          existingCards.map(c =>
            c.machineId === card.machineId
              ? { ...c, previousShiftOutput: prevOutput, previousShiftOperators: prevOperators, previousShiftDate: prevDate, previousShiftName: prevShiftName }
              : c
          )
        );
      });
    });
  }

  private fetchMachineMonthlyShiftOutputs(
    cards: MachineCardData[],
    dateParams: { year: number; month: number }
  ): void {
    cards.forEach(card => {
      this.operationShiftService.getShiftMonthlyOutputByMachine({
        year: dateParams.year,
        month: dateParams.month,
        machine: card.machineId,
      }).pipe(catchError(() => of(null))).subscribe(res => {
        if (!res?.data || !Array.isArray(res.data)) return;
        
        const shifts = res.data as { shiftId: number; shiftName: string; monthlyOutput: number; makeReadyCount?: number }[];
        if (shifts.length === 0) return;

        let totalMonthly = 0;
        let totalMakeReady = 0;
        let maxOutput = -1;

        shifts.forEach(s => {
          totalMonthly += s.monthlyOutput;
          totalMakeReady += s.makeReadyCount ?? 0;
          if (s.monthlyOutput > maxOutput) maxOutput = s.monthlyOutput;
        });

        const mappedShifts = shifts.map(s => ({
          shiftId: s.shiftId,
          shiftName: s.shiftName,
          monthlyOutput: s.monthlyOutput,
          makeReadyCount: s.makeReadyCount ?? 0,
          isBest: s.monthlyOutput === maxOutput && s.monthlyOutput > 0
        }));

        this.machineCards.update(existingCards =>
          existingCards.map(c =>
            c.machineId === card.machineId
              ? { 
                  ...c, 
                  monthlyTotalOutput: totalMonthly,
                  monthlyTotalMakeReady: totalMakeReady,
                  monthlyShiftOutputs: mappedShifts
                }
              : c
          )
        );
      });
    });
  }

  /**
   * Returns delta between current actualImpression and previous shift output.
   * Positive = ahead, Negative = behind.
   */
  getPreviousShiftDelta(card: MachineCardData): { delta: number; isAhead: boolean; pct: number } | null {
    if (card.previousShiftOutput === null) return null;
    const prev = card.previousShiftOutput;
    const curr = this.getCurrentShiftImpression(card).actualOutput;
    const delta = curr - prev;
    const pct   = prev > 0 ? Math.round(Math.abs(delta / prev) * 100) : 0;
    return { delta: Math.abs(delta), isAhead: delta >= 0, pct };
  }

  /**
   * Returns the actualOutput and makeReadyCount for the current active shift
   * by matching currentShiftId against the shiftWiseDetails from getDailyReport.
   * Falls back to card totals if no match is found.
   */
  getCurrentShiftImpression(card: MachineCardData): { actualOutput: number; makeReadyCount: number } {
    if (card.currentShiftId && card.shiftWiseDetails?.length) {
      const match = card.shiftWiseDetails.find(s => s.shiftId === card.currentShiftId);
      if (match) return { actualOutput: match.actualOutput, makeReadyCount: match.makeReadyCount };
    }
    // fallback to totals
    return { actualOutput: card.actualImpression, makeReadyCount: card.makeReadyCount };
  }

  /**
   * Format 'HH:MM:SS' or 'HH:MM' time string to '6:00 AM' display format.
   * Returns empty string if input is empty.
   */
  formatShiftTime(time: string): string {
    if (!time) return '';
    const [hStr, mStr] = time.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12  = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  /**
   * Returns the appropriate icon type for a shift name.
   * 'day' | 'night' | 'morning' | 'evening'
   */
  getShiftIcon(shiftName: string): 'day' | 'night' | 'morning' | 'evening' {
    const name = shiftName?.toLowerCase() ?? '';
    if (name.includes('night')) return 'night';
    if (name.includes('morning') || name.includes('first')) return 'morning';
    if (name.includes('evening') || name.includes('second') || name.includes('afternoon')) return 'evening';
    return 'day';
  }

  // ─── WebSocket (purely additive — page works 100% without it) ────────────

  private initWebSocketListeners(): void {
    this.wsSubs.push(
      this.wsService.connected$.subscribe(connected => {
        this.wsConnected.set(connected);
      })
    );

    this.wsSubs.push(
      this.wsService.dashboardUpdate$.subscribe(data => {
        if (Array.isArray(data?.data)) {
          this.operatorPerformance.set(data.data);
        } else if (Array.isArray(data)) {
          this.operatorPerformance.set(data);
        }

        this.liveUpdateFlash.set(true);
        this.lastUpdatedTime.set(new Date());
        setTimeout(() => this.liveUpdateFlash.set(false), 2_000);
      })
    );
  }

  /**
   * Fire-and-forget WS subscription.
   * Any error is caught and logged — never propagates up.
   */
  private trySubscribeWebSocket(params: ReturnType<typeof this.searchParams>): void {
    try {
      this.wsService.subscribe({
        year:        params.year,
        month:       params.month,
        department:  params.department as number,
        machineType: params.machineType,
        machine:     null,
      });
    } catch (err) {
      console.warn('[OperatorDashboardWs] Subscribe failed, live updates unavailable:', err);
    }
  }

  // ─── General methods ──────────────────────────────────────────────────────

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  selectTab(tab: MachineTypeDTO): void {
    this.activeTab.set(tab);
  }

  onDepartmentChange(departmentId: number): void {
    this.departmentId.set(departmentId);
  }

  onDateChange(): void {
    this.fetchPerformanceData();
  }

  getAllMachineTypes(): void {
    this.machineTypeService.getAll().subscribe({
      next: (result) => {
        if (result.data?.length) {
          this.machineTypes.set(result.data);
        }
      },
      error: () => {
        this.notification.set({ type: 'error', message: 'Failed to fetch machine types.' });
      },
    });
  }

  public fetchPerformanceData(): void {
    this.isVisible.set(false);
    setTimeout(() => {
      this.operatorService.performance(this.searchParams()).pipe(
        tap(response => {
          const perfData = response?.data?.data || [];
          this.operatorPerformance.set(perfData);
          this.isVisible.set(true);
          this.fetchOperatorNcrData(perfData);
        }),
        catchError(() => {
          this.notification.set({ type: 'error', message: 'Failed to fetch Job Operation data' });
          this.isVisible.set(true);
          return of([]);
        })
      ).subscribe();
    }, 300);
  }

  private fetchOperatorNcrData(operators: OperatorPerformanceDTO[]): void {
    if (!operators || operators.length === 0) {
      this.operatorNcrCounts.set({});
      this.operatorNcrData.set({});
      this.operatorNcrTotals.set({});
      return;
    }

    const year = this.selectedYear();
    const monthNum = this.selectedMonth();
    const monthStr = String(monthNum).padStart(2, '0');
    const startDate = `${year}-${monthStr}-01`;
    const lastDay = new Date(year, monthNum, 0).getDate();
    const endDate = `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

    const empNos = new Set<string>();
    operators.forEach(op => {
      const empNo = op.empNo || (op as any).employeeNo;
      if (empNo) empNos.add(empNo);
    });

    empNos.forEach(empNo => {
      this.operatorService.getNcr({
        employeeNo: empNo,
        startDate,
        endDate,
        status: 'approved',
        itemsPerPage: -1
      }, false).subscribe({
        next: (res: any) => {
          const dataArray = res?.data?.data || res?.data || [];
          let safeDataArray = Array.isArray(dataArray) ? dataArray : [];
          
          // Filter only approved records
          safeDataArray = safeDataArray.filter((ncr: any) => !ncr.status || ncr.status.toLowerCase() === 'approved');
          
          const count = safeDataArray.length;
          const totalVal = safeDataArray.reduce((sum: number, ncr: any) => sum + (Number(ncr.ncrValue) || 0), 0);

          this.operatorNcrCounts.set({
            ...this.operatorNcrCounts(),
            [empNo]: count
          });
          this.operatorNcrData.set({
            ...this.operatorNcrData(),
            [empNo]: safeDataArray
          });
          this.operatorNcrTotals.set({
            ...this.operatorNcrTotals(),
            [empNo]: totalVal
          });
        },
        error: (err) => {
          console.error(`Error fetching NCR data for employee ${empNo}:`, err);
        }
      });
    });
  }

  protected getOperatorIrCount(empNo: string | undefined): number {
    if (!empNo) return 0;
    const ncrList = this.operatorNcrData()[empNo] || [];
    return ncrList.filter((ncr: any) => ncr.ncrType?.toUpperCase() === 'IRCC').length;
  }

  protected getOperatorCcCount(empNo: string | undefined): number {
    if (!empNo) return 0;
    const ncrList = this.operatorNcrData()[empNo] || [];
    return ncrList.filter((ncr: any) => ncr.ncrType?.toUpperCase() === 'CC').length;
  }

  protected getOperatorNcrTotalValue(empNo: string | undefined): number {
    if (!empNo) return 0;
    return this.operatorNcrTotals()[empNo] || 0;
  }

  public fetchDepartment(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: (result) => {
        if (result.data?.departments) {
          this.$$departmentDTO.set(result.data.departments);
        }
      },
      error: (error) => {
        console.error('Error fetching departments:', error);
      },
    });
  }

  protected sortedOperatorsByOutput = computed(() => {
    return [...this.operatorPerformance()]
      .map(op => ({
        ...op,
        avgHourlyOutput: op.totalHoursWorked
          ? Math.round(op.totalOutput / op.totalHoursWorked)
          : 0,
      }))
      .sort((a, b) => b.totalOutput - a.totalOutput);
  });

  /**
   * For each machine ID, returns the top-2 operators ranked by their
   * per-machine totalOutput (from OperatorMachinePerformanceDTO).
   * Avg output is derived as operator.totalOutput / operator.totalHoursWorked.
   */
  protected topOperatorsByMachine = computed(() => {
    const result = new Map<number, { operatorName: string; machineOutput: number; avgOutput: number; makeReadyCount: number }[]>();

    const operators = this.operatorPerformance();
    if (!operators.length) return result;

    // Collect all machine IDs present in performance data
    const machineIds = new Set<number>();
    operators.forEach(op => op.machines.forEach(m => machineIds.add(m.machineId)));

    machineIds.forEach(machineId => {
      const ranked = operators
        .map(op => {
          const machine = op.machines.find(m => m.machineId === machineId);
          if (!machine) return null;
          return {
            operatorName: op.operatorName,
            machineOutput: machine.totalOutput,
            avgOutput: op.totalHoursWorked
              ? Math.round(machine.totalOutput / op.totalHoursWorked)
              : 0,
            makeReadyCount: op.makeReadyCount ?? 0,
          };
        })
        .filter((x): x is { operatorName: string; machineOutput: number; avgOutput: number; makeReadyCount: number } => x !== null)
        .sort((a, b) => b.machineOutput - a.machineOutput)
        .slice(0, 2);

      result.set(machineId, ranked);
    });

    return result;
  });

  /** Get top operators for a specific machine card */
  getTopOperatorsForMachine(machineId: number): { operatorName: string; machineOutput: number; avgOutput: number; makeReadyCount: number }[] {
    return this.topOperatorsByMachine().get(machineId) ?? [];
  }

  /** Get comparison operators: Top vs You */
  getComparisonOperators(card: MachineCardData): { operatorName: string; machineOutput: number; avgOutput: number; makeReadyCount: number; isYou: boolean; isTop: boolean }[] {
    const operators = this.operatorPerformance();
    if (!operators.length) return [];

    const machineOps = operators
      .map(op => {
        const machine = op.machines.find(m => m.machineId === card.machineId);
        if (!machine) return null;
        return {
          operatorName: op.operatorName,
          machineOutput: machine.totalOutput,
          avgOutput: op.totalHoursWorked ? Math.round(machine.totalOutput / op.totalHoursWorked) : 0,
          makeReadyCount: op.makeReadyCount ?? 0,
        };
      })
      .filter((x): x is { operatorName: string; machineOutput: number; avgOutput: number; makeReadyCount: number } => x !== null)
      .sort((a, b) => b.machineOutput - a.machineOutput);

    if (machineOps.length === 0) return [];

    const topOp = machineOps[0];
    const currentRosterNames = card.currentRosterOperators.map(n => n.toLowerCase());
    const youOp = machineOps.find(op => currentRosterNames.includes(op.operatorName.toLowerCase()));

    const result = [];
    
    if (youOp && youOp.operatorName === topOp.operatorName) {
      result.push({ ...topOp, isTop: true, isYou: true });
      if (machineOps.length > 1) result.push({ ...machineOps[1], isTop: false, isYou: false });
    } else {
      result.push({ ...topOp, isTop: true, isYou: false });
      if (youOp) {
        result.push({ ...youOp, isTop: false, isYou: true });
      } else if (machineOps.length > 1) {
        result.push({ ...machineOps[1], isTop: false, isYou: false });
      }
    }
    
    return result;
  }

  /** Get the monthly output of the top operator for a specific machine */
  getTopOperatorMonthlyOutput(machineId: number): number {
    const topOps = this.getTopOperatorsForMachine(machineId);
    return topOps.length > 0 ? topOps[0].machineOutput : 0;
  }

  /** Get the monthly output of the current shift operator for a specific machine */
  getShiftOperatorMonthlyOutput(card: MachineCardData): number {
    if (!card.currentRosterOperators || card.currentRosterOperators.length === 0) return 0;
    
    const opName = card.currentRosterOperators[0];
    const operators = this.operatorPerformance();
    const op = operators.find(o => o.operatorName.trim().toLowerCase() === opName.trim().toLowerCase());
    
    if (!op) return 0;
    
    const machinePerf = op.machines.find(m => m.machineId === card.machineId);
    return machinePerf ? machinePerf.totalOutput : 0;
  }

  getOperatorComparisonPercentage(card: MachineCardData): number {
    const shiftOp = this.getShiftOperatorMonthlyOutput(card);
    const topOp = this.getTopOperatorMonthlyOutput(card.machineId);
    if (topOp === 0) return shiftOp > 0 ? 100 : 0;
    return Math.round((shiftOp / topOp) * 100);
  }

  /** Get gap to Top Operator for current operator */
  getOperatorGap(card: MachineCardData): { behindCount: number; behindPct: number; isTop: boolean; topOperatorName: string; unit: string } | null {
    const compOps = this.getComparisonOperators(card);
    if (!compOps.length) return null;

    const topOp = compOps.find(op => op.isTop);
    const youOp = compOps.find(op => op.isYou);

    if (!topOp) return null;

    // If there's no You operator or You is the Top operator
    if (!youOp || youOp.isTop) {
      return {
        behindCount: 0,
        behindPct: 100,
        isTop: true,
        topOperatorName: topOp.operatorName,
        unit: card.unitOfMachine || 'imp'
      };
    }

    const behindCount = topOp.machineOutput - youOp.machineOutput;
    const behindPct = topOp.machineOutput > 0 ? Math.round((youOp.machineOutput / topOp.machineOutput) * 100) : 100;

    return {
      behindCount,
      behindPct,
      isTop: false,
      topOperatorName: topOp.operatorName,
      unit: card.unitOfMachine || 'imp'
    };
  }

  protected displayOperators = computed(() => {
    const ops = this.sortedOperatorsByOutput();
    if (ops.length === 0) return [];
    
    // Keep original index for rendering ranks correctly even if duplicated
    const mapped = ops.map((op, i) => ({ ...op, originalIndex: i }));
    
    if (this.duplicateRows()) {
      return [...mapped, ...mapped];
    }
    return mapped;
  });


  getGridColCount(): number {
    // Always use 3 columns so card size remains consistent even if there are fewer than 3 machines
    return 3;
  }

  /** Auto-rotation for machine cards */
  private startCardRotation(): void {
    // Auto-rotation disabled; using manual pagination
  }

  nextCardPage(): void {
    const totalCards = this.machineCards().length;
    const totalPages = Math.ceil(totalCards / 3);
    if (totalPages > 1) {
      this.currentCardPage.set((this.currentCardPage() + 1) % totalPages);
    }
  }

  prevCardPage(): void {
    const totalCards = this.machineCards().length;
    const totalPages = Math.ceil(totalCards / 3);
    if (totalPages > 1) {
      this.currentCardPage.set((this.currentCardPage() - 1 + totalPages) % totalPages);
    }
  }

  private stopCardRotation(): void {
    if (this.cardRotationInterval) {
      clearInterval(this.cardRotationInterval);
    }
  }

  /** Helper: CSS class for machine status badge */
  machineStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'operational': return 'od-badge-online';
      case 'make ready':  return 'od-badge-online';
      case 'idle':        return 'od-badge-idle';
      case 'maintenance': return 'od-badge-running'; // blue
      case 'hold':        return 'od-badge-hold';    // red
      default:            return 'od-badge-idle';
    }
  }

  /** Helper: CSS class for job status badge */
  jobStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'in progress': return 'od-badge-running';
      case 'hold':        return 'od-badge-hold';
      case 'make ready':  return 'od-badge-inprogress';
      case 'completed':   return 'od-badge-online';
      default:            return 'od-badge-idle';
    }
  }

  /** Helper: Calculate percentage for current job */
  getJobProgressPct(job: any): number {
    if (!job) return 0;
    const planned = job.operationQuantity || 0;
    const actual = job.noOfSheets || 0;
    if (planned === 0) return 0;
    return Math.min(100, Math.round((actual / planned) * 100));
  }

  /** Helper: progress bar colour class */
  progressClass(pct: number): string {
    // Return empty to always keep the bar blue
    return '';
  }

  /** Convert decimal hours → 'Xh Ym' (e.g. 3.63 → '3h 38m') */
  formatHours(decimalHours: number): string {
    if (!decimalHours && decimalHours !== 0) return '—';
    const h = Math.floor(decimalHours);
    const m = Math.round((decimalHours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }

  // ─── Auto-Scroll Logic for TV Dashboard ──────────────────────────────────
  private startAutoScroll(): void {
    this.autoScrollInterval = setInterval(() => {
      const wrap = this.tableWrap?.nativeElement;
      if (!wrap) return;
      
      const maxScroll = wrap.scrollHeight - wrap.clientHeight;
      
      // Dynamically duplicate rows if content exceeds the screen to create a seamless circle
      if (maxScroll > 0 && !this.duplicateRows()) {
        this.duplicateRows.set(true);
        return; // wait for next tick so DOM can update with duplicated rows
      }
      
      if (maxScroll <= 0) return;
      
      // Scroll down seamlessly
      wrap.scrollBy(0, 1);
      
      // Infinite circular scroll logic
      const tbody = wrap.querySelector('tbody');
      if (tbody && this.duplicateRows()) {
        // Because rows are duplicated exactly once, half the tbody height equals one full set
        const halfHeight = tbody.scrollHeight / 2;
        
        if (wrap.scrollTop >= halfHeight) {
          // Snap back by exactly half the height to maintain the seamless visual loop
          wrap.scrollTop = wrap.scrollTop - halfHeight;
        }
      }
    }, 40); // 40ms interval = ~25px per second
  }

  private stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }

  // ─── Export to Excel ─────────────────────────────────────────────────────

  exportToExcel(): void {
    const operators = this.sortedOperatorsByOutput();
    if (!operators.length) {
      this.notification.set({ type: 'error', message: 'No data to export.' });
      return;
    }

    const monthLabel = this.months[this.selectedMonth() - 1]?.label ?? '';
    const year       = this.selectedYear();
    const tab        = this.activeTab()?.machineTypeName ?? 'All';

    // ── Build rows ──
    const rows = operators.map((op, i) => ({
      'Rank':             i + 1,
      'Operator Name':    op.operatorName,
      'Monthly Output':   op.totalOutput,
      'Avg Output':       op.avgHourlyOutput,
      'Planned Hours (h)': op.plannedHours,
      'Operation Hours (h)': op.totalHoursWorked,
      'Hold Hours (h)':   op.totalHoursHold,
      'Make Ready':       op.makeReadyCount,
      'IR Count':         this.getOperatorIrCount(op.empNo),
      'CC Count':         this.getOperatorCcCount(op.empNo),
      'NCR Value (LKR)':  this.getOperatorNcrTotalValue(op.empNo),
      'Days Worked':      op.daysWorked,
      'Machines':         op.machines.map((m: any) => m.machineName).join(', '),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // ── Column widths ──
    ws['!cols'] = [
      { wch: 6  },  // Rank
      { wch: 28 },  // Operator Name
      { wch: 16 },  // Monthly Output
      { wch: 22 },  // Avg Output
      { wch: 18 },  // Planned Hours
      { wch: 20 },  // Operation Hours
      { wch: 16 },  // Hold Hours
      { wch: 14 },  // Make Ready
      { wch: 12 },  // IR Count
      { wch: 12 },  // CC Count
      { wch: 18 },  // NCR Value
      { wch: 14 },  // Days Worked
      { wch: 36 },  // Machines
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operator Performance');

    const filename = `Operator_Performance_${tab}_${monthLabel}_${year}.xlsx`;
    XLSX.writeFile(wb, filename);
  }


  getOperatorShiftReport(machineId: number, machineName: string): void {
  this.machineService.getOperatorShiftReport(
    machineId,
    {
      year: this.selectedYear(),
      month: this.selectedMonth()
    }
  ).subscribe({
    next: (res) => {
      this.exportOperatorShiftReport(res.data, machineName);
    },
    error: (err) => console.error(err)
  });
}

  exportOperatorShiftReport(
    data: OperatorShiftReportDTO[],
    machineName: string
  ): void {

    if (!data || data.length === 0) {
      this.notification.set({
        type: 'error',
        message: 'No data available to export.'
      });
      return;
    }

    const rows = data.map(item => ({
      'Date': item.actualDate,
      'Operator': item.operatorName,
      'Shift': item.shiftName,
      'Worked Hours': Number(Number(item.workedHours ?? 0).toFixed(2)),
      'Total MR Hours': Number(Number(item.makeReadyHours ?? 0).toFixed(2)),
      'Make Ready': item.numberOfMakeReady,
      'Average MR Time(h)': Number((Number(item.numberOfMakeReady) ? Number(item.makeReadyHours ?? 0) / Number(item.numberOfMakeReady) : 0).toFixed(2)),
      'Average MR Time(mins)': Math.round((Number(item.numberOfMakeReady) ? Number(item.makeReadyHours ?? 0) / Number(item.numberOfMakeReady) : 0) * 60),
      'Average Output / Hr': item.avgOutputPerHr ?? 0,
      'Total Output': Number(item.totalImp)
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet['!cols'] = [
      { wch: 15 }, // Date
      { wch: 30 }, // Operator
      { wch: 15 }, // Shift
      { wch: 15 }, // Worked Hours
      { wch: 15 }, // MR Hours
      { wch: 15 }, // Make Ready
      { wch: 15 }, // Make Ready Avg (h)
      { wch: 22 }, // Make Ready Avg (mins)
      { wch: 20 }, // Avg Output
      { wch: 18 }  // Total Output
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Operator Shift Report'
    );

    const month = this.months.find(
      m => m.value === this.selectedMonth()
    )?.label;

    const fileName =
      `${machineName}_Operator_Report_${month}_${this.selectedYear()}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  }

  getJobReport(machineId: number, machineName: string): void {
    this.machineService.getShiftJobDetailsReport(
      machineId,
      {
        year: this.selectedYear(),
        month: this.selectedMonth()
      }
    ).subscribe({
      next: (res) => {
        if (!res.data || res.data.length === 0) {
          this.notification.set({
            type: 'error',
            message: 'No data available to export.'
          });
          return;
        }

        const rows: any[] = [];
        res.data.forEach((shift: any) => {
          if (shift.jobs && shift.jobs.length > 0) {
            shift.jobs.forEach((job: any) => {
              rows.push({
                'Date': shift.actualDate,
                'Shift': shift.shiftName,
                'Job No': job.jobNo,
                'Job Description': job.jobDescription,
                'Customer': job.jobCustomer,
                'Operators': job.operators ? job.operators.map((o: any) => o.operatorName).join(', ') : '',
                'Total Output': job.totalImp,
                'Worked Hours': job.workedHours,
                'Operational Hours': job.operationalHours,
                'Make Ready Hours': job.makeReadyHours,
                'Make Ready Count': job.makeReadyCount,
                'Avg Output / Hr': job.avgOutputPerHr
              });
            });
          }
        });

        if (rows.length === 0) {
          this.notification.set({ type: 'error', message: 'No jobs found in the report.' });
          return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);

        worksheet['!cols'] = [
          { wch: 15 }, // Date
          { wch: 15 }, // Shift
          { wch: 20 }, // Job No
          { wch: 30 }, // Job Description
          { wch: 25 }, // Customer
          { wch: 30 }, // Operators
          { wch: 18 }, // Total Output
          { wch: 15 }, // Worked Hours
          { wch: 18 }, // Operational Hours
          { wch: 18 }, // Make Ready Hours
          { wch: 18 }, // Make Ready Count
          { wch: 18 }  // Avg Output/Hr
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Job Report');

        const month = this.months.find(
          m => m.value === this.selectedMonth()
        )?.label;
        const fileName = `${machineName}_Job_Report_${month}_${this.selectedYear()}.xlsx`;

        XLSX.writeFile(workbook, fileName);
      },
      error: (err) => console.error(err)
    });
  }
}